// src/redux/messageSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';
import { logoutUser } from './auth';

const MSG_BASE = `${process.env.REACT_APP_API_URL}/api/messages`;

// Thin fetch wrapper that mirrors the rest of the app's auth/CSRF handling.
const apiFetch = async (url, options = {}, getState) => {
  const token = getState?.()?.auth?.user?.token;
  const csrf = getCookie('XSRF-TOKEN');
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrf ? { 'X-XSRF-TOKEN': csrf } : {}),
      ...options.headers
    }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || 'Request failed');
  return data;
};

// ───────── Thunks ─────────

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (_, { getState, rejectWithValue }) => {
    try {
      return await apiFetch(`${MSG_BASE}/conversations`, {}, getState);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchMessageUnreadCount = createAsyncThunk(
  'messages/fetchUnreadCount',
  async (_, { getState, rejectWithValue }) => {
    try {
      const data = await apiFetch(`${MSG_BASE}/unread-count`, {}, getState);
      return data.count;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const startConversation = createAsyncThunk(
  'messages/startConversation',
  async ({ targetId, targetModel }, { getState, rejectWithValue }) => {
    try {
      const data = await apiFetch(
        `${MSG_BASE}/conversations`,
        { method: 'POST', body: JSON.stringify({ targetId, targetModel }) },
        getState
      );
      return data.conversation;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchConversation = createAsyncThunk(
  'messages/fetchConversation',
  async (conversationId, { getState, rejectWithValue }) => {
    try {
      const data = await apiFetch(`${MSG_BASE}/conversations/${conversationId}`, {}, getState);
      return data.conversation;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async ({ conversationId, before }, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (before) params.set('before', before);
      const qs = params.toString();
      const data = await apiFetch(
        `${MSG_BASE}/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`,
        {},
        getState
      );
      return { conversationId, messages: data.messages, hasMore: data.hasMore, prepend: !!before };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async ({ conversationId, text }, { getState, rejectWithValue }) => {
    try {
      const data = await apiFetch(
        `${MSG_BASE}/conversations/${conversationId}/messages`,
        { method: 'POST', body: JSON.stringify({ text }) },
        getState
      );
      return { conversationId, message: data.message };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchContacts = createAsyncThunk(
  'messages/fetchContacts',
  async (q = '', { getState, rejectWithValue }) => {
    try {
      const qs = q ? `?q=${encodeURIComponent(q)}` : '';
      const data = await apiFetch(`${MSG_BASE}/contacts${qs}`, {}, getState);
      return data.contacts;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const checkCanMessage = createAsyncThunk(
  'messages/checkCanMessage',
  async ({ targetId, targetModel }, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ targetId, targetModel });
      return await apiFetch(`${MSG_BASE}/can-message?${params}`, {}, getState);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ───────── Slice ─────────

const initialState = {
  conversations: [],
  conversationsStatus: 'idle',
  unreadTotal: 0,
  byId: {},            // conversationId -> { messages, hasMore, status, meta }
  contacts: [],
  contactsStatus: 'idle',
  activeId: null,
  sendStatus: 'idle',
  error: null
};

const ensureThread = (state, id) => {
  if (!state.byId[id]) {
    state.byId[id] = { messages: [], hasMore: true, status: 'idle', meta: null };
  }
  return state.byId[id];
};

const upsertConversation = (state, conv) => {
  const idx = state.conversations.findIndex(c => c._id === conv._id);
  if (idx === -1) {
    state.conversations.unshift(conv);
  } else {
    state.conversations[idx] = { ...state.conversations[idx], ...conv };
  }
};

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setActiveConversation(state, { payload }) {
      state.activeId = payload;
    },
    clearActiveConversation(state) {
      state.activeId = null;
    },
    clearMessageError(state) {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // Conversations list
      .addCase(fetchConversations.pending, state => { state.conversationsStatus = 'loading'; })
      .addCase(fetchConversations.fulfilled, (state, { payload }) => {
        state.conversationsStatus = 'succeeded';
        state.conversations = payload.conversations;
        state.unreadTotal = payload.conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      })
      .addCase(fetchConversations.rejected, (state, { payload }) => {
        state.conversationsStatus = 'failed';
        state.error = payload;
      })

      // Unread badge
      .addCase(fetchMessageUnreadCount.fulfilled, (state, { payload }) => {
        state.unreadTotal = payload;
      })

      // Start / open a conversation
      .addCase(startConversation.fulfilled, (state, { payload }) => {
        upsertConversation(state, payload);
        const t = ensureThread(state, payload._id);
        t.meta = payload;
      })

      // Fetch a single conversation's meta
      .addCase(fetchConversation.fulfilled, (state, { payload }) => {
        upsertConversation(state, payload);
        const t = ensureThread(state, payload._id);
        t.meta = payload;
      })

      // Messages
      .addCase(fetchMessages.pending, (state, { meta }) => {
        const t = ensureThread(state, meta.arg.conversationId);
        t.status = 'loading';
      })
      .addCase(fetchMessages.fulfilled, (state, { payload }) => {
        const t = ensureThread(state, payload.conversationId);
        t.status = 'succeeded';
        if (payload.prepend) {
          // Older page: merge before existing, de-duplicating by id.
          const existingIds = new Set(t.messages.map(m => m._id));
          const older = payload.messages.filter(m => !existingIds.has(m._id));
          t.messages = [...older, ...t.messages];
          t.hasMore = payload.hasMore;
        } else {
          // Newest page: merge any optimistic/new messages we may already hold.
          const incomingIds = new Set(payload.messages.map(m => m._id));
          const localExtras = t.messages.filter(m => !incomingIds.has(m._id) && !m._id.startsWith('temp-'));
          t.messages = [...payload.messages, ...localExtras].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          // Reading the newest page clears unread locally.
          const conv = state.conversations.find(c => c._id === payload.conversationId);
          if (conv && conv.unreadCount) {
            state.unreadTotal = Math.max(0, state.unreadTotal - conv.unreadCount);
            conv.unreadCount = 0;
          }
        }
      })
      .addCase(fetchMessages.rejected, (state, { meta, payload }) => {
        const t = ensureThread(state, meta.arg.conversationId);
        t.status = 'failed';
        state.error = payload;
      })

      // Send
      .addCase(sendMessage.pending, state => { state.sendStatus = 'sending'; })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        state.sendStatus = 'succeeded';
        const t = ensureThread(state, payload.conversationId);
        if (!t.messages.some(m => m._id === payload.message._id)) {
          t.messages.push(payload.message);
        }
        const conv = state.conversations.find(c => c._id === payload.conversationId);
        if (conv) {
          conv.lastMessage = {
            text: payload.message.text,
            senderId: payload.message.sender,
            senderModel: payload.message.senderModel,
            createdAt: payload.message.createdAt
          };
          conv.lastMessageAt = payload.message.createdAt;
          // Bump to top.
          state.conversations = [
            conv,
            ...state.conversations.filter(c => c._id !== conv._id)
          ];
        }
      })
      .addCase(sendMessage.rejected, (state, { payload }) => {
        state.sendStatus = 'failed';
        state.error = payload;
      })

      // Contacts
      .addCase(fetchContacts.pending, state => { state.contactsStatus = 'loading'; })
      .addCase(fetchContacts.fulfilled, (state, { payload }) => {
        state.contactsStatus = 'succeeded';
        state.contacts = payload;
      })
      .addCase(fetchContacts.rejected, (state, { payload }) => {
        state.contactsStatus = 'failed';
        state.error = payload;
      })

      // Reset on logout
      .addCase(logoutUser.fulfilled, () => initialState);
  }
});

export const { setActiveConversation, clearActiveConversation, clearMessageError } = messageSlice.actions;
export default messageSlice.reducer;
